import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledGlobalSearch } from '../../../styles';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import { useTranslation } from 'react-i18next';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import { useLocation } from 'react-router-dom';
import { PersonList } from '../../../redux/Candidate/Candidate.type';
import styled, { css } from 'styled-components';
import CardComponent, { StyledCard } from '../../Card.component';
import BaseModalComponent from '../BaseModal.component';
import InputComponent from '../../Input/Input.component';
import SearchIcon from '../../Icons/Search.icon';
import { getCandidateSearchThunk } from '../../../redux/Candidate/Candidate.thunk';
import { dateFormatter } from '../../../utils/formatters';
import { DATE_TIME_FORMAT } from '../../../constants/date.contant';
import NotAssignComponent from '../../NotAssign.component';
import BadgeComponent from '../../Badge.component';
import CandidatePreviewModalComponent from '../Candidate/CandidatePreviewModal.component';
import { CANDIDATES_GROUPS } from '../../../types/candidates.types';
import TableRowComponent from '../../Table/TableRow.component';
import { debounce, uniqueId } from 'lodash';
import NoResultComponent from '../../NoResult.component';
import { addInternshipCandidateThunk } from '../../../redux/Internship/Internship.thunk';
import SpinnerComponent from '../../Spinner.component';
import ButtonComponent from '../../Button.component';
import SelectRegionModal from './SelectRegionModal';
import { SelectOptionProps } from '../../Input/Select.component';
import {
  ResponseData,
  AddInternshipCandidateThunkPayload,
} from '../../../redux/Internship/Internship.thunk';
import ErrorAlertComponent from '../../Alert/ErrorAlert.component';
import { AxiosError } from 'axios';

const StyledStylesRoot = css`
  max-height: 600px;
  min-height: 600px;
  max-width: 860px;
  min-width: 860px;
`;

const StyledResultCardComponent = styled(StyledCard)`
  overflow: auto;
  ${StyledStylesRoot}
`;

const StyledEmptyResult = styled(StyledCard)`
  display: flex;
  align-items: center;
  justify-content: center;
  ${StyledStylesRoot}
`;

const StyledGlobalSearchWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 32px;
  align-items: center;
  gap: 10px;
`;

export interface InternshipAddCandidateModalProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  internshipId?: string;
  internshipRegions: {
    id: number;
    name: string;
    code: string;
  }[];
  activeCandidates: PersonList[];
}

const DEBOUNCE_TIME = 300;

function isAxiosError(
  res: AxiosError<ResponseData, any> | AddInternshipCandidateThunkPayload,
): res is AxiosError<ResponseData, any> {
  return (res as AxiosError<ResponseData, any>).response !== undefined;
}

const InternshipAddCandidateModal = ({
  internshipId,
  internshipRegions,
  activeCandidates,
}: InternshipAddCandidateModalProps) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const { openModal, closeModal } = useModalManager();
  const location = useLocation();
  const [firstSearch, setFirstSearch] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [list, setList] = useState<PersonList[]>([]);
  const [search, setSearch] = useState('');

  const onSearch = async (search: string): Promise<void> => {
    try {
      const result = await dispatch(
        getCandidateSearchThunk({
          search,
          pageSize: 15,
          page: 1,
        }),
      );
      setList(result.payload.data);
    } finally {
      setLoading(false);
      setFirstSearch(false);
    }
  };

  useEffect(() => {
    onSearch('');
  }, []);

  const searchDebounce = useCallback(debounce(onSearch, DEBOUNCE_TIME), []);

  const handleInputChange = (searchValue: string) => {
    setSearch(searchValue);
    searchDebounce(searchValue);
  };

  const handleSearch = (value: string) => {
    setLoading(true);
    dispatch(
      getCandidateSearchThunk({ search: value, pageSize: 15, page: 1 }),
    ).then(({ payload }: any) => {
      setLoading(false);
      if (!payload.data) return;
      setFirstSearch(false);
      setList(payload.data);
    });
  };

  const prepareRowColumns = (item: PersonList) => {
    const date = item.date ? dateFormatter(item.date, DATE_TIME_FORMAT) : '-';

    return [
      date,
      item.fullName,
      <NotAssignComponent mode="badge" has={!!item.job}>
        {item.job}
      </NotAssignComponent>,
      <NotAssignComponent mode="badge" has={!!item.experience}>
        <BadgeComponent
          appearance="default"
          text={item.experience ? item.experience.name : ''}
        />
      </NotAssignComponent>,
      <NotAssignComponent mode="badge" has={!!item.status}>
        <BadgeComponent appearance="default" text={item.status!} />
      </NotAssignComponent>,
    ];
  };

  const handleCandidateClick = (id: number | string) => {
    openModal(
      CandidatePreviewModalComponent,
      {
        personId: id,
        candidateGroup: CANDIDATES_GROUPS.ALL,
        additionalAction: (
          <ButtonComponent
            handleClick={() => handleCandidateDBClick(Number(id))}
            customColor="rgb(0, 128, 0)"
          >
            {t('Add candidate')}
          </ButtonComponent>
        ),
      },
      {
        position: 'right-top',
      },
    );
  };

  const handleCandidateDBClick = (personID: number) => {
    async function addInternshipCandidate(region?: string) {
      const payload: AddInternshipCandidateThunkPayload =
        region === undefined
          ? { id: personID }
          : { id: personID, regionId: region };

      closeModal();
      const result = await dispatch(
        addInternshipCandidateThunk({ id: internshipId!, payload: payload }),
      );
      if (isAxiosError(result)) {
        openModal(ErrorAlertComponent, {
          header: t('Error'),
          children: i18n.exists('alreadyAddedToInternship')
            ? t('alreadyAddedToInternship')
            : result?.response?.data?.message || t('Unknown status'),
        });
      }
    }

    if (internshipRegions.length > 1) {
      const regionOptions: SelectOptionProps[] = internshipRegions.map(r => ({
        label: r.name,
        value: r.id,
      }));
      openModal(BaseModalComponent, {
        hasHeader: false,
        children: (
          <SelectRegionModal
            label={t('Choose regions')}
            placeholder={t('Regions')}
            options={regionOptions}
            onConfirmClick={addInternshipCandidate}
          />
        ),
      });
    } else {
      addInternshipCandidate();
    }
  };

  const renderSearchList = useMemo(() => {
    return list.map((item: PersonList) => {
      return (
        <TableRowComponent
          key={uniqueId('table-rows-')}
          columns={prepareRowColumns(item)}
          handleClick={() => handleCandidateClick(item.id)}
          handleDBClick={() => handleCandidateDBClick(item.id)}
          doubleClickHint={t('addToInternshipHint')}
        />
      );
    });
  }, [list, location]);

  const renderBlockSearchList = useMemo(() => {
    if (firstSearch) {
      return (
        <StyledResultCardComponent>
          <SpinnerComponent size="large" />
        </StyledResultCardComponent>
      );
    }

    if (!list.length)
      return (
        <CardComponent>
          <StyledEmptyResult>
            <NoResultComponent />
          </StyledEmptyResult>
        </CardComponent>
      );

    return (
      <StyledResultCardComponent>{renderSearchList}</StyledResultCardComponent>
    );
  }, [firstSearch, list, renderSearchList]);

  return (
    <StyledGlobalSearch>
      <BaseModalComponent hasHeader={false} rightBlock={renderBlockSearchList}>
        <StyledGlobalSearchWrapper>
          <InputComponent
            autoFocus
            disabled={isLoading}
            placeholder={t('Search name, surname, phone, email')}
            value={search}
            onChange={handleInputChange}
            onEnter={handleSearch}
            mode="transparent"
          />
          <SearchIcon />
        </StyledGlobalSearchWrapper>
      </BaseModalComponent>
    </StyledGlobalSearch>
  );
};

export default InternshipAddCandidateModal;
